import { useEffect } from "react";
import { useNavigate } from "react-router";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { ServicePartnerPageDetailsDto } from "@/api/types";
import bgPlaceholder from "@/assets/images/image-placeholder.png";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { AudiencePreview } from "@/components/AudienceSelector/AudiencePreview";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { IconButton } from "@/components/Button/IconButton";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Mail01Icon } from "@/components/Icons/Mail01Icon";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { PhoneIcon } from "@/components/Icons/PhoneIcon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { UsersCheckIcon } from "@/components/Icons/UsersCheckIcon";
import { AutoTranslatedLabel } from "@/components/Label/AutoTranslatedLabel";
import { CreatedAtAndUpdatedAtLabel } from "@/components/Label/CreatedAtAndUpdatedAtLabel";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { ShareButton } from "@/components/ShareButton/ShareButton";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { Tooltip } from "@/design-system/Tooltip";
import { isContentAutoTranslated } from "@/helpers/languages";
import { isDefined } from "@/helpers/util";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { BringmeDetailsView } from "@/modules/service-partners/components/BringmeDetails/BringmeDetailsView";
import { IqbiDetailsView } from "@/modules/service-partners/components/IqbiDetails/IqbiDetailsView";
import { MyPupDetailsView } from "@/modules/service-partners/components/MyPupDetails/MyPupDetailsView";
import { ServiceDetailsBlockCard } from "@/modules/service-partners/components/ServiceDetailsBlockCard";
import { canCreateNewServicePartner } from "@/modules/service-partners/permissions";
import { serviceMutations } from "@/queries/services/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export interface ServicePartnerDetailsProps {
  serviceDetails: ServicePartnerPageDetailsDto;
  onDelete: (serviceId: string) => Promise<unknown>;
}
export function ServicePartnerDetails({ serviceDetails, onDelete }: ServicePartnerDetailsProps): React.ReactNode {
  const projectId = useProjectId();
  const slug = useSlug();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const navigate = useNavigate();
  const { componentProps: deleteModalProps, openDeleteModal } =
    useDeleteModal<DeleteRequestParams>("service-delete-modal");
  const resolveImage = useImageResolver();
  const hasPermission = usePermission();

  const { mutate: updateServiceViewCount, isSuccess: isSuccessUpdateServiceViewCount } =
    serviceMutations.useUpdateServiceViewCount();

  // Update view count on page mount
  useEffect(() => {
    if (isSuccessUpdateServiceViewCount) {
      return;
    }

    updateServiceViewCount({
      serviceId: serviceDetails.id,
    });
  }, [serviceDetails.id, updateServiceViewCount, isSuccessUpdateServiceViewCount]);

  const handleDeleteService = async (entity: DeleteRequestParams) => {
    await onDelete(entity.id);

    void navigate(routes.servicePartners.list({ slug }));
  };

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      value: "edit",
      icon: <Edit05Icon />,
      "data-testid": "context-menu-edit-btn",
      callbackFn: () => {
        void navigate(
          routes.servicePartners.edit({
            slug,
            id: serviceDetails.id,
          }),
          {
            state: {
              from: "details",
            },
          },
        );
      },
      isHidden: !serviceDetails.canEdit,
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      value: "delete",
      icon: <Trash02Icon />,
      "data-testid": "context-menu-delete-btn",
      callbackFn: () => {
        openDeleteModal({
          id: serviceDetails.id,
        });
      },
      isHidden: !serviceDetails.canDelete,
    },
  ];
  const bgImage = serviceDetails.headerImage || sessionUser.project.backgroundImage;
  const isServiceDescriptionShown =
    serviceDetails.type !== "iqbi" || serviceDetails.partnerProperties.iqbi?.status === "noConsent";
  const serviceActions = (
    <Inline gap="sm" align="center" justify="end">
      {serviceDetails.phoneNumber && (
        <IconButton
          type="link"
          variant="soft"
          href={`tel:${serviceDetails.phoneNumber}`}
          icon={<PhoneIcon />}
          title={`Phone number of ${serviceDetails.title}`}
          withTooltip={false}
        />
      )}
      {serviceDetails.email && (
        <IconButton
          type="link"
          variant="soft"
          href={`mailto:${serviceDetails.email}`}
          icon={<Mail01Icon />}
          title={`Email address of ${serviceDetails.title}`}
          withTooltip={false}
        />
      )}
      <ShareButton entityId={serviceDetails.id} entityType="service" />
      <ContextMenu items={menuActions} />
    </Inline>
  );

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={serviceDetails.title.trim() || t("page.service-details.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.service.list.title"),
              to: routes.servicePartners.list({
                slug,
              }),
            },
            {
              name: t("page.service-details.title"),
            },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: [projectId] }}
          context={{ scope: "project", entity: "service-partner", page: "detail", entityId: serviceDetails.id }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.service-partner-detail.suggestion.1"),
            t("component.ask-nobi-chat-drawer.service-partner-detail.suggestion.2"),
          ]}
        />
      }
    >
      <PageGrid.Row>
        <PageGrid.Column size="50%">
          <Stack gap="md">
            <ServiceDetailsBlockCard
              title={serviceDetails.title}
              subtitle={serviceDetails.subtitle}
              backgroundImage={bgImage ? resolveImage(bgImage, "intrinsic") : bgPlaceholder}
              thumbnailImage={serviceDetails.profileImage?.url}
              renderBadges={() => (
                <>
                  {serviceDetails.connection && (
                    <Badge key="connection-name" icon={<MarkerPin01Icon />}>
                      {serviceDetails.connection?.name}
                    </Badge>
                  )}
                  {serviceDetails.hasAudience && sessionUser.isAdmin && <Badge icon={<UsersCheckIcon />} />}
                </>
              )}
            >
              {isDefined(serviceDetails.viewCount) ? (
                <Spread space="md">
                  <Tooltip tooltip={t("common.tooltip.view-count-disclaimer")}>
                    <span className="text-body whitespace-nowrap text-text-secondary">
                      {t("common.label.view", {
                        count: serviceDetails.viewCount,
                      })}
                    </span>
                  </Tooltip>
                  {serviceActions}
                </Spread>
              ) : (
                serviceActions
              )}

              {serviceDetails.audience.length > 0 &&
                sessionUser.isAdmin &&
                hasPermission(canCreateNewServicePartner) && (
                  <aside className="w-fit">
                    <AudiencePreview audience={serviceDetails.audience} readOnly />
                  </aside>
                )}
              {isServiceDescriptionShown && (
                <section
                  className="prose whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{
                    __html: serviceDetails.description!,
                  }}
                />
              )}
              {isContentAutoTranslated(serviceDetails.originalLanguageId, sessionUser) && <AutoTranslatedLabel />}
              {(serviceDetails.phoneNumber || serviceDetails.email) && (
                <section className="my-space-md">
                  <Stack gap="xs">
                    {serviceDetails.phoneNumber && (
                      <Inline gap="sm" align="center">
                        <Inline gap="xs" align="center" wrap={false}>
                          <PhoneIcon tone="secondary" />
                          <span className="text-text-secondary">{t("model.service.phone-number")}:</span>
                        </Inline>
                        <Button variant="ghostSecondary" type="link" href={`tel:${serviceDetails.phoneNumber}`}>
                          {serviceDetails.phoneNumber}
                        </Button>
                      </Inline>
                    )}
                    {serviceDetails.email && (
                      <Inline gap="sm" align="center">
                        <Inline gap="xs" align="center" wrap={false}>
                          <Mail01Icon tone="secondary" />
                          <span className="text-text-secondary">{t("model.service.email")}:</span>
                        </Inline>
                        <Button variant="ghostSecondary" type="link" href={`mailto:${serviceDetails.email}`}>
                          {serviceDetails.email}
                        </Button>
                      </Inline>
                    )}
                  </Stack>
                </section>
              )}
              <CreatedAtAndUpdatedAtLabel createdAt={serviceDetails.createdAt} updatedAt={serviceDetails.updatedAt} />
            </ServiceDetailsBlockCard>

            {serviceDetails.type === "iqbi" && (
              <IqbiDetailsView
                {...{
                  serviceDetails,
                }}
              />
            )}
            {serviceDetails.type === "myPup" && serviceDetails.partnerProperties.myPup && (
              <MyPupDetailsView
                {...{
                  serviceDetails,
                }}
              />
            )}
            {serviceDetails.type === "bringme" && <BringmeDetailsView serviceDetails={serviceDetails} />}
          </Stack>
        </PageGrid.Column>
      </PageGrid.Row>
      <DeleteModal<DeleteRequestParams>
        title={t("model.service.action.delete.confirmation")}
        description={t("model.service.action.delete.description")}
        onDelete={handleDeleteService}
        {...deleteModalProps}
      />
    </DocumentPage>
  );
}
interface DeleteRequestParams {
  id: string;
}
